import { useOutletContext } from "react-router";

import useBreadcrumbs from "components/Breadcrumbs/useBreadcrumbs";
import PaginationIndicator from "components/PaginationIndicator";
import SearchInput from "components/SearchInput";
import NotFoundPage from "components/error/NotFoundPage";
import { SEARCH_PLACEHOLDERS } from "constants/searchPlaceholders";
import PageInfoHeader from "ds/components/PageInfoHeader";
import TableSimple from "ds/components/TableSimple";
import useAnalytics from "hooks/useAnalytics";
import useErrorHandle from "hooks/useErrorHandle";
import useTitle from "hooks/useTitle";
import { IntegrationsOutletContext } from "views/Account/Integrations/types";

import VCSIntegrationEmpty from "./Empty";
import VCS_INTEGRATION_USED_BY_TABLE_CELLS from "./Table/Cells";
import { SORT_CONFIG, VCS_INTEGRATION_USED_BY_TABLE_FILTERS_STORAGE_KEY } from "./Table/constants";
import useVCSIntegrationUsedByTableStore from "./Table/useTableStore";
import { useSearchVCSIntegrationUsedBy } from "./useSearchVCSIntegrationUsedBy";

const VCSIntegrationNewUsedBy = () => {
  const { integration: integrationDetails } = useOutletContext<IntegrationsOutletContext>();
  const {
    integration,
    usedByItems,
    isPageLoading,
    isPageEmpty,
    isPageRefetching,
    totalEntitiesCount,
    error,
    hasNextPage,
    loadMoreItems,
  } = useSearchVCSIntegrationUsedBy();

  const trackAnalytics = useAnalytics();

  useTitle(integration?.name ? `Used by · ${integration.name}` : "Used by");

  useBreadcrumbs(
    [
      {
        title: "Integrations",
        link: "/integrations",
      },
      {
        title: integrationDetails.name,
        link: `/integrations/${integrationDetails.id}`,
      },
      {
        title: integration?.name || "",
      },
    ],
    [integration?.name]
  );

  const ErrorContent = useErrorHandle(error);

  if (ErrorContent) {
    return ErrorContent;
  }

  if (!integration) {
    return <NotFoundPage />;
  }

  return (
    <>
      <PageInfoHeader title="Used by">
        {!isPageEmpty && (
          <>
            <PaginationIndicator
              currentCount={usedByItems.length}
              totalCount={totalEntitiesCount}
              loading={isPageRefetching}
              minimumLoadingDuration={200}
            />

            <SearchInput
              placeholder={SEARCH_PLACEHOLDERS.VCS_USED_BY}
              filtersOrderSettingsKey={VCS_INTEGRATION_USED_BY_TABLE_FILTERS_STORAGE_KEY}
            />
          </>
        )}
      </PageInfoHeader>

      <TableSimple
        data={usedByItems}
        itemIdKey="stackId"
        loading={isPageLoading}
        store={useVCSIntegrationUsedByTableStore}
        ariaLabel={`${integration.name} used by`}
        onLoadMore={hasNextPage ? loadMoreItems : undefined}
        trackAnalytics={trackAnalytics}
        cellPerColumn={VCS_INTEGRATION_USED_BY_TABLE_CELLS}
        filtersStorageKey={VCS_INTEGRATION_USED_BY_TABLE_FILTERS_STORAGE_KEY}
        sortConfig={SORT_CONFIG}
        emptyState={<VCSIntegrationEmpty />}
        virtualize={usedByItems.length >= 50}
      />
    </>
  );
};

export default VCSIntegrationNewUsedBy;
